import {useEffect, useState} from "react";
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from "../../../basic/dialog";
import {Button} from "../../../basic/button";
import {Shield, ShoppingBag} from "lucide-react";
import {toast} from "sonner";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {useErrorHandler} from "../../../../hooks/useErrorHandler";
import {
    useGetAdminEntitlementsQuery,
    useUpdateProductByIdMutation,
    type VwpEntitlement
} from "../../../../store/generated/api.ts";
import {ProductForm} from "./ProductForm";
import {EntitlementSelector} from "../../../shared/selectors/EntitlementSelector";
import {CollapsibleSection} from "../../../shared/CollapsibleSection";
import {type ProductData} from "../../../../types/product";

interface EditProductDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    selectedProduct: {
        id: string;
        name: string;
        description?: string;
        stripeProductId: string;
        totalLicenses: number;
        activeSubscriptions: number;
        softwareDurationDays?: number;
        group?: string;
        priority?: number;
        entitlements?: VwpEntitlement[];
    } | null;
}

export function EditProductDialog({
                                      open,
                                      onOpenChange,
                                      selectedProduct
                                  }: EditProductDialogProps) {
    const {t} = useTranslationWithKeys();
    const {showErrorToast, handleApiError} = useErrorHandler();
    const [editedProductData, setEditedProductData] = useState<ProductData>({
        name: '',
        description: '',
        stripeProductId: '',
        softwareDurationDays: undefined,
        group: undefined,
        priority: undefined
    });
    const [selectedEntitlements, setSelectedEntitlements] = useState<VwpEntitlement[]>([]);

    const [updateProductById, {isLoading: isUpdating}] = useUpdateProductByIdMutation();

    const {
        data: entitlementsData,
        isLoading: entitlementsLoading,
        error: entitlementsError
    } = useGetAdminEntitlementsQuery();

    const availableEntitlements = entitlementsData?.availableEntitlements || [];

    useEffect(() => {
        if (selectedProduct) {
            setEditedProductData({
                name: selectedProduct.name,
                description: selectedProduct.description || '',
                stripeProductId: selectedProduct.stripeProductId,
                softwareDurationDays: selectedProduct.softwareDurationDays,
                group: selectedProduct.group,
                priority: selectedProduct.priority
            });
            setSelectedEntitlements(selectedProduct.entitlements || []);
        }
    }, [selectedProduct]);

    const handleEntitlementsChange = (entitlements: VwpEntitlement[]) => {
        setSelectedEntitlements(entitlements);
    };

    const handleSave = async () => {
        if (!selectedProduct) return;

        if (!editedProductData.name?.trim()) {
            showErrorToast({message: t('admin.products.edit.validation.nameRequired')});
            return;
        }

        try {
            await updateProductById({
                productId: selectedProduct.id,
                vwpUpdateProductRequest: {
                    name: editedProductData.name,
                    stripeProductId: editedProductData.stripeProductId,
                    softwareDurationDays: editedProductData.softwareDurationDays,
                    group: editedProductData.group,
                    priority: editedProductData.priority,
                    entitlements: selectedEntitlements
                }
            }).unwrap();

            toast.success(t('admin.products.edit.success', {productName: editedProductData.name}));
            onOpenChange(false);
        } catch (error) {
            handleApiError(error, {fallbackMessage: t('admin.products.edit.errors.updateFailed')});
        }
    };

    const handleCancel = () => {
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-[90vw] max-w-7xl max-h-[90vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>{t('admin.products.edit.title')}</DialogTitle>
                    <DialogDescription>
                        {t('admin.products.edit.description')}
                    </DialogDescription>
                </DialogHeader>

                {selectedProduct && (
                    <div className="flex-1 overflow-y-auto">
                        <div className="space-y-6 py-4">
                            <div className="flex items-center gap-3 pb-4 border-b">
                                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                                    <ShoppingBag className="h-6 w-6 text-primary"/>
                                </div>
                                <div>
                                    <h3 className="font-medium">{selectedProduct.name}</h3>
                                    <p className="text-muted-foreground">{t('admin.products.edit.productId')}: {selectedProduct.id}</p>
                                    <p className="text-muted-foreground">{t('admin.products.edit.activeSubscriptions')}: {selectedProduct.activeSubscriptions}</p>
                                </div>
                            </div>

                            <ProductForm
                                editedProductData={editedProductData}
                                onProductDataChange={setEditedProductData}
                            />

                            <CollapsibleSection
                                title={t('admin.products.edit.entitlements.title')}
                                icon={<Shield className="h-5 w-5"/>}
                                defaultOpen={false}
                            >
                                {entitlementsLoading && (
                                    <div className="flex items-center justify-center py-8">
                                        <p>{t('common.loading')}</p>
                                    </div>
                                )}

                                {entitlementsError && (
                                    <div className="flex items-center justify-center py-8">
                                        <div className="text-center">
                                            <p className="text-red-500">{t('common.error')}</p>
                                            <p className="text-sm text-muted-foreground mt-1">
                                                {t('admin.products.edit.entitlements.loadError')}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {!entitlementsLoading && !entitlementsError && (
                                    <EntitlementSelector
                                        availableEntitlements={availableEntitlements}
                                        selectedEntitlements={selectedEntitlements}
                                        onEntitlementsChange={handleEntitlementsChange}
                                        loading={entitlementsLoading}
                                        error={entitlementsError ? t('admin.products.edit.entitlements.loadError') : undefined}
                                    />
                                )}
                            </CollapsibleSection>
                        </div>
                    </div>
                )}

                <DialogFooter className="flex-shrink-0">
                    <Button variant="outline" onClick={handleCancel}>
                        {t('common.cancel')}
                    </Button>
                    <Button onClick={handleSave} disabled={isUpdating}>
                        {isUpdating ? t('common.saving') : t('common.saveChanges')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}